import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCampaign } from '../../../hooks/useCampaign'
import { CampaignFooter } from '../CampaignFooter'
import { useTheme } from '@mui/material/styles'
import { CampaignPageOverlay } from './CampaignPageOverlay'

type CampaignPageProps = {
  children: React.ReactNode
  absolutePosition?: boolean
}

function CampaignHeader() {
  const { logo, headerText } = useCampaign()
  const theme = useTheme()

  return (
    <Stack
      sx={{
        px: 3,
        py: 2,
        alignItems: 'center',
        justifyContent: 'center',
        position: {
          xs: 'fixed',
          sm: 'static',
        },
        top: 0,
        left: 0,
        width: '100%',
        height: {
          xs: 72,
          sm: 'auto',
        },
        bgcolor: {
          xs: 'rgba(255, 255, 255, .8)',
          sm: 'rgba(255, 255, 255, .95)',
        },
        borderTopLeftRadius: {
          sm: 8,
        },
        borderTopRightRadius: {
          sm: 8,
        },
        backdropFilter: 'blur(16px)',
        zIndex: 2,
        boxShadow: {
          xs: theme.customShadows.card,
          sm: 'none',
        },
      }}>
      {logo ? (
        <Box component="img" src={logo} sx={{ height: { xs: 40, sm: 60 } }} />
      ) : (
        <Typography variant="subtitle1" textAlign="center">
          {headerText}
        </Typography>
      )}
    </Stack>
  )
}

function CampaignPageFull(props: CampaignPageProps) {
  const { pageBackgroundColor, loading, primaryColor, whitelabel, logo, headerText } = useCampaign()
  const theme = useTheme()

  const showHeader = !!logo || !!headerText

  return (
    <Stack
      sx={{
        position: props.absolutePosition ? 'absolute' : 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: {
          xs: 'background.neutral',
          sm: pageBackgroundColor,
        },
        overflow: 'auto',
        transition: '.2s background-color ease-in-out',
        px: {
          xs: 0,
          sm: 1,
        },
        py: {
          xs: 0,
          sm: 2,
        },
      }}>
      <Stack
        gap={{
          sm: 3,
        }}
        sx={{
          width: '100%',
          maxWidth: {
            sm: 640,
          },
          margin: {
            sm: 'auto',
          },
          height: {
            xs: '100%',
            sm: 'auto',
          },
          pt: showHeader
            ? {
                xs: 9, // Offset for fixed header
                sm: 0,
              }
            : 0,
        }}>
        <CampaignPageOverlay type="circles" />
        <Stack
          sx={{
            borderRadius: {
              xs: 0,
              sm: 1,
            },
            boxShadow: {
              sm: theme.customShadows.card,
            },
            flexGrow: 1,
            position: 'relative',
            zIndex: 2,
          }}>
          {showHeader && <CampaignHeader />}
          <Stack
            sx={{
              background: {
                sm: 'rgba(255, 255, 255, .8)',
              },
              backdropFilter: {
                sm: 'blur(16px)',
              },
              ...(showHeader
                ? {
                    borderBottomLeftRadius: {
                      sm: 8,
                    },
                    borderBottomRightRadius: {
                      sm: 8,
                    },
                  }
                : {
                    borderRadius: {
                      sm: 1,
                    },
                  }),
              p: {
                xs: 2,
                sm: 3,
              },
              position: 'relative',
              flexGrow: 1,
              overflow: 'auto',
              justifyContent: 'center',
            }}>
            {loading && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: primaryColor,
                  },
                }}
              />
            )}
            {props.children}
          </Stack>
        </Stack>
        {!whitelabel && <CampaignFooter />}
      </Stack>
    </Stack>
  )
}

function CampaignPageCompact(props: CampaignPageProps) {
  const { loading, primaryColor, whitelabel } = useCampaign()

  return (
    <Stack
      gap={2}
      sx={{
        bgcolor: 'background.neutral',
        borderRadius: 1,
      }}>
      <Stack
        sx={{
          borderRadius: {
            sm: 1,
          },
          p: {
            xs: 2,
            sm: 3,
          },
          position: 'relative',
          flexGrow: 1,
          overflow: 'auto',
          justifyContent: 'center',
        }}>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              '& .MuiLinearProgress-bar': {
                backgroundColor: primaryColor,
              },
            }}
          />
        )}
        {props.children}
      </Stack>
      {!whitelabel && <CampaignFooter />}
    </Stack>
  )
}

export function CampaignPage(props: CampaignPageProps) {
  const { campaignFrame } = useCampaign()
  if (campaignFrame.inFrame && campaignFrame.frameView !== 'full')
    return <CampaignPageCompact {...props}>{props.children}</CampaignPageCompact>

  return <CampaignPageFull {...props}>{props.children}</CampaignPageFull>
}
